import { workerRegistry } from "@/lib/agents/registry"
import { NodeProps } from '@xyflow/react'
import { Search, ArrowRight, Plus, Trash2, Edit } from "lucide-react"
import { InlineHandles, NodeHandlers, WorkerLabeledHandle } from "../handles"
import { useWorker } from "../hooks"
import { NodeLayout } from './node'
import { ConditionHandler } from "../condition"
import { MemoizedWorker } from "../memoizedworkers"
import { useState, useCallback, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { app } from "@/lib/app"

const { lookup } = workerRegistry

lookup.icon = Search

function ValueEditor({ 
  entry, 
  index, 
  updateEntry, 
  removeEntry 
}: { 
  entry: { key: string; value: string }
  index: number
  updateEntry: (index: number, field: "key" | "value", value: string) => void
  removeEntry: (index: number) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [tempValue, setTempValue] = useState(entry.value)

  useEffect(() => {
    setTempValue(entry.value)
  }, [entry.value])

  const handleSave = () => {
    updateEntry(index, "value", tempValue)
    setIsOpen(false)
  }

  return (
    <div className="flex gap-2 items-center border rounded p-2">
      <Input
        placeholder="Key"
        value={entry.key}
        onChange={(e) => updateEntry(index, "key", e.target.value)}
        className="text-sm flex-1"
      />
      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="flex-1 flex items-center gap-1">
        <Input
          placeholder="Value"
          value={entry.value}
          onChange={(e) => updateEntry(index, "value", e.target.value)}
          className="text-sm flex-1"
        />
        <Dialog open={isOpen} onOpenChange={(open) => {
          setIsOpen(open)
          if (open) {
            setTempValue(entry.value)
          }
        }}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={(e) => {
                e.stopPropagation()
                setIsOpen(true)
              }}
              type="button"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Value</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Key</label>
                <Input value={entry.key} readOnly className="bg-muted" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Value</label>
                <Textarea
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="min-h-[200px] resize-y"
                  placeholder="Enter value..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setTempValue(entry.value)
                  setIsOpen(false)
                }}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-red-500 hover:text-red-700 shrink-0"
        onClick={() => removeEntry(index)}
        type="button"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}

function Parameters({ worker }: { worker: LookupWorker }) {
  const lookupTable = worker.parameters.lookupTable || {}
  
  // Convert lookup table object to array for easier editing
  const [entries, setEntries] = useState<Array<{ key: string; value: string }>>(() => {
    return Object.entries(lookupTable).map(([key, value]) => ({ key, value }))
  })

  // Update worker parameters when entries change
  useEffect(() => {
    const newTable: Record<string, string> = {}
    entries.forEach(({ key, value }) => {
      if (key.trim()) {
        newTable[key.trim()] = value
      }
    })
    worker.parameters.lookupTable = newTable
    worker.updateWorker()
    app.agent.update()
  }, [entries, worker])

  const addEntry = useCallback(() => {
    setEntries([...entries, { key: "", value: "" }])
  }, [entries])

  const removeEntry = useCallback((index: number) => {
    setEntries(entries.filter((_, i) => i !== index))
  }, [entries])

  const updateEntry = useCallback((index: number, field: "key" | "value", value: string) => {
    const newEntries = [...entries]
    newEntries[index] = { ...newEntries[index], [field]: value }
    setEntries(newEntries)
  }, [entries])

  const [defaultValue, setDefaultValue] = useState<string>(worker.parameters.defaultValue || "")

  useEffect(() => {
    worker.parameters.defaultValue = defaultValue
    worker.updateWorker()
    app.agent.update()
  }, [defaultValue, worker])

  return (
    <div className="p-2 flex flex-col gap-2 w-full grow">
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Default Value</label>
        <Input
          placeholder="Default value when lookup fails"
          value={defaultValue}
          onChange={(e) => setDefaultValue(e.target.value)}
          className="text-sm"
        />
      </div>
      <div className="space-y-2 max-h-[400px] overflow-y-scroll pr-3">
        {entries.map((entry, index) => (
          <ValueEditor
            key={index}
            entry={entry}
            index={index}
            updateEntry={updateEntry}
            removeEntry={removeEntry}
          />
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={addEntry}
        type="button"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Key-Value Pair
      </Button>
    </div>
  )
}

export function LookupNode(props: NodeProps) {
  const worker = useWorker<LookupWorker>(props.id)
  return (
    <NodeLayout worker={worker} resizable className="flex flex-col h-full" minHeight={300} minWidth={350}>
      <InlineHandles>
        <WorkerLabeledHandle handler={worker.fields.input} />
        <WorkerLabeledHandle handler={worker.fields.output} />
      </InlineHandles>
      <MemoizedWorker worker={worker} name="parameters">
        <Parameters worker={worker} />
      </MemoizedWorker>
      <NodeHandlers worker={worker} />
      <ConditionHandler />
    </NodeLayout>
  )
}

